<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('detailProject.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('detailProject.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('detailProject.criteria') }}</legend>
                  <div class="row">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label class="required">{{
                          $t('detailProject.dateRange')
                        }}</label>
                        <a-range-picker
                          format="DD-MM-YYYY"
                          v-model="model.dateRange"
                          value-format="DD-MM-YYYY"
                          :ranges="{
                            Today: [moment(), moment()],
                            'This Month': [
                              moment().startOf('month'),
                              moment().endOf('month')
                            ]
                          }"
                          class="ts-w-full"
                        >
                          <template slot="renderExtraFooter"></template>
                        </a-range-picker>
                        <div class="validate" v-if="errors.has('dateRange')">
                          {{ errors.first('dateRange') }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>{{ $t('detailProject.project') }}</label>
                        <a-select
                          v-model="model.project_id"
                          :options="projects"
                          showSearch
                          :filterOption="
                            (input, option) =>
                              option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                          "
                          placeholder="All"
                          mode="multiple"
                          class="ts-w-full"
                          :max-tag-count="1"
                          @change="onProjectChange"
                        >
                        </a-select>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>{{ $t('detailProject.task') }}</label>
                        <a-select
                          v-model="model.task_id"
                          :options="tasks"
                          :max-tag-count="1"
                          showSearch
                          :filterOption="
                            (input, option) =>
                              option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                          "
                          placeholder="All"
                          mode="multiple"
                          class="ts-w-full"
                        >
                        </a-select>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
                      <ts-button @click="onPreview" color="primary">{{
                        $t('preview')
                      }}</ts-button>
                      <ts-button @click="exportExcel" color="info">{{
                        $t('excel')
                      }}</ts-button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper></ts-card
        >
      </div>
      <div class="col-md-12 ts-mt-3">
        <ts-card>
          <ts-table :columns="columns" :records="resources" :loading="loading">
            <template v-slot="{ record }">
              <td class="ts-whitespace-no-wrap">{{ record.project_name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.milestone_name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.task_name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.barcode }}</td>
              <td class="ts-whitespace-no-wrap">
                {{ record.product_name_en }}
              </td>
              <td class="ts-whitespace-no-wrap">{{ record.payment_date }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.uom_name_en }}</td>
              <td class="ts-whitespace-no-wrap">
                {{ record.start_point ? record.start_point : '-' }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ splitLocation(record.start_location) }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.end_point ? record.end_point : '-' }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ splitLocation(record.end_location) }}
              </td>
              <td class="ts-w-40 ts-text-right">{{ record.quantity }}</td>
              <td class="ts-w-40 ts-text-right">{{ record.estimate_qty }}</td>
              <td class="ts-w-40 ts-text-right">{{ record.borrowing_ref }}</td>
              <td>
                <a :href="record.file_path_view" target="_blank">
                  <img
                    :src="record.file_path_view"
                    alt=""
                    v-if="record.file_path_view"
                    class="ts-text-center ts-block ts-w-8 ts-h-8 ts-rounded  ts-object-center"
                  />
                </a>
              </td>
              <td>
                <a :href="record.file_path_end_view" target="_blank">
                  <img
                    :src="record.file_path_end_view"
                    alt=""
                    v-if="record.file_path_end_view"
                    class="ts-text-center ts-block ts-w-8 ts-h-8 ts-rounded  ts-object-center"
                  />
                </a>
              </td>
              <td class="ts-whitespace-no-wrap">{{ record.customer_name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.owner_name }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.project_status }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.remark }}</td>
            </template>
            <template v-slot:tfooter>
              <td class="ts-whitespace-no-wrap">
                Total Record: {{ resources.length }}
              </td>
              <td colspan="9"></td>
              <td class="ts-text-right">{{ sumByCol('quantity') }}</td>
              <td class="ts-text-right">{{ sumByCol('estimate_qty') }}</td>
              <td colspan="6"></td>
            </template>
          </ts-table>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { sumBy, trim } from 'lodash'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'

export default {
  name: 'detailProject',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      projects: [],
      tasks: [],
      resources: [],
      model: {
        dateRange: [
          moment()
            .startOf('month')
            .format('DD-MM-YYYY'),
          moment()
            .endOf('month')
            .format('DD-MM-YYYY')
        ],
        project_id: undefined,
        task_id: undefined
      }
    }
  },
  computed: {
    columns() {
      return [
        { name: this.$t('detailProject.project') },
        { name: this.$t('detailProject.milestone') },
        { name: this.$t('detailProject.task') },
        { name: this.$t('detailProject.barcode') },
        { name: this.$t('detailProject.productName') },
        { name: this.$t('detailProject.date') },
        { name: this.$t('detailProject.uom') },
        { name: this.$t('detailProject.startPoint') },
        { name: this.$t('detailProject.startLocation') },
        { name: this.$t('detailProject.endPoint') },
        { name: this.$t('detailProject.endLocation') },
        { name: this.$t('detailProject.qty'), style: 'width: 100px' },
        { name: this.$t('detailProject.estimateQty'), style: 'width: 100px' },
        {
          name: this.$t('detailProject.borrowingRef'),
          style: 'width: 150px'
        },
        { name: this.$t('detailProject.Pic1') },
        { name: this.$t('detailProject.Pic2') },
        { name: this.$t('detailProject.customer') },
        { name: this.$t('detailProject.owner') },
        { name: this.$t('detailProject.status') },
        { name: this.$t('detailProject.remark') }
      ]
    },
    splitLocation: () => locationString => {
      if (typeof locationString == 'string') {
        const array = JSON.parse(locationString)
        return array.join(',')
      }
      return ''
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    moment,
    ...mapActions('report/project', [
      'getProject',
      'getTask',
      'detailProject',
      'detailProjectExcel'
    ]),
    sumByCol(column) {
      return sumBy(this.resources, o => (o[column] ? parseFloat(o[column]) : 0))
    },
    async fetchData() {
      this.loading = true
      await this.getProject()
        .then(response => {
          this.projects = response.map(el => ({
            value: el.project_id,
            label: el.project_name
          }))
        })
        .finally(() => {
          this.loading = false
        })
    },
    onProjectChange() {
      this.loading = true
      this.tasks = []
      this.model.task_id = undefined
      this.getTask({
        project_id: this.model.project_id
      })
        .then(result => {
          this.tasks = result.map(el => ({
            value: el.id,
            label: el.title
          }))
        })
        .catch(err => {
          this.$toasted.error(err.message)
        })
        .finally(() => (this.loading = false))
    },
    onPreview() {
      this.loading = true
      this.detailProject(this.model)
        .then(result => {
          this.resources = result.data
        })
        .catch(err => {
          this.$toasted.error(err.message)
          this.errors = new Errors(err.errors)
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/project/detailProjectExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    }
  }
}
</script>
